import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contractAPI } from '../../services/api';
import { useBranchFilter } from '../../contexts/BranchFilterContext';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import { generateContractPDF } from '../../utils/pdfGenerator';
import { saveAs } from 'file-saver';
import './ContractList.css';

const ContractList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedBranchId } = useBranchFilter();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [printingContractId, setPrintingContractId] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showContractTypeModal, setShowContractTypeModal] = useState(false);

  useEffect(() => {
    loadContracts();
  }, [page, selectedBranchId]);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const response = await contractAPI.getAll(searchTerm, page, pageSize, selectedBranchId);
      setContracts(response.data.items || []);
      const totalCount = response.data.totalCount || 0;
      setTotalPages(Math.ceil(totalCount / pageSize) || 1);
    } catch (err) {
      setErrorMessage(t('contract.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadContracts();
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await contractAPI.delete(id);
        setSuccessMessage(t('contract.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadContracts();
      } catch (err) {
        setErrorMessage(t('contract.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('contract.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('contract.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await contractAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('contract.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadContracts();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('contract.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('contract.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(contracts.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handlePrint = async (contract) => {
    try {
      setPrintingContractId(contract.id);
      // Fetch full print-ready details
      const response = await contractAPI.getForPrint(contract.id);
      const contractDetails = response.data;
      await generateContractPDF(contract, contractDetails);
      setSuccessMessage(t('contract.list.messages.pdfSuccess'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage(t('contract.list.messages.pdfError'));
      setShowErrorModal(true);
    } finally {
      setPrintingContractId(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (format) {
        case 'excel':
          response = await contractAPI.exportExcel();
          filename = `Contracts_${new Date().toISOString().slice(0, 10)}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await contractAPI.exportCsv();
          filename = `Contracts_${new Date().toISOString().slice(0, 10)}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await contractAPI.exportPdf();
          filename = `Contracts_${new Date().toISOString().slice(0, 10)}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('contract.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('contract.list.messages.exportError', { format: format.toUpperCase() }));
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
        <div className="page-actions-left">
          <button className="btn-add" onClick={() => setShowContractTypeModal(true)}>
            + {t('contract.list.addNew')}
          </button>
          <div className="search-container">
              <input
                type="text"
              className="search-input"
                placeholder={t('contract.list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
            <button className="search-icon" onClick={handleSearch}>
              <i className="fas fa-search"></i>
              </button>
            </div>
        </div>
        <div className="action-buttons">
          <div className="export-menu-container">
          <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
            <i className="fas fa-file-export"></i>
          </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={() => handleExport('excel')}>
                  <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                </button>
                <button onClick={() => handleExport('csv')}>
                  <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                </button>
                <button onClick={() => handleExport('pdf')}>
                  <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                </button>
              </div>
            )}
          </div>
          <button className="action-btn action-btn-delete" title={t('contract.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
            <i className="fas fa-trash"></i>
          </button>
        </div>
          </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
          setShowConfirmModal(false);
        }}
        onCancel={() => setShowConfirmModal(false)}
      />

      {/* Contract Type Selection Modal */}
      {showContractTypeModal && (
        <div className="modal-overlay" onClick={() => setShowContractTypeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">{t('contract.list.selectContractType')}</h5>
              <button type="button" className="btn-close" onClick={() => setShowContractTypeModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center', padding: '2rem' }}>
                <button
                  className="btn btn-primary"
                  style={{ padding: '2rem 4rem', fontSize: '1.2rem' }}
                  onClick={() => {
                    navigate('/contract/create?type=residential');
                    setShowContractTypeModal(false);
                  }}
                >
                  <i className="fas fa-home" style={{ marginRight: '0.5rem' }}></i>
                  {t('contract.list.types.residential')}
                </button>
                <button
                  className="btn btn-primary"
                  style={{ padding: '2rem 4rem', fontSize: '1.2rem' }}
                  onClick={() => {
                    navigate('/contract/create?type=commercial');
                    setShowContractTypeModal(false);
                  }}
                >
                  <i className="fas fa-building" style={{ marginRight: '0.5rem' }}></i>
                  {t('contract.list.types.commercial')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                  onChange={handleSelectAll}
                        checked={selectedIds.length === contracts.length && contracts.length > 0}
                      />
                    </th>
                    <th>{t('contract.list.columns.id')}</th>
              <th>{t('contract.list.columns.property')}</th>
              <th>{t('contract.list.columns.type')}</th>
              <th>{t('contract.list.columns.dateFrom')}</th>
              <th>{t('contract.list.columns.dateTo')}</th>
              <th>{t('contract.list.columns.status')}</th>
              <th>{t('contract.list.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.length === 0 ? (
                    <tr>
                <td colSpan="8" className="text-center py-4">
                        {t('contract.list.messages.noData')}
                      </td>
                    </tr>
                  ) : (
                    contracts.map((contract) => (
                      <tr key={contract.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(contract.id)}
                            onChange={() => handleSelectItem(contract.id)}
                          />
                        </td>
                        <td>{contract.id}</td>
                        <td>{contract.property?.nameEng || 'N/A'}</td>
                        <td>{contract.contractType === 1 ? t('contract.list.types.commercial') : t('contract.list.types.residential')}</td>
                        <td>{contract.dateFrom ? new Date(contract.dateFrom).toLocaleDateString() : 'N/A'}</td>
                        <td>{contract.dateTo ? new Date(contract.dateTo).toLocaleDateString() : 'N/A'}</td>
                        <td>
                    <span className="status-badge">
                            {contract.isActive ? t('contract.list.status.active') : t('contract.list.status.inactive')}
                          </span>
                        </td>
                        <td>
                    <div className="action-buttons-cell">
                      <Link
                        to={`/contract/view/${contract.id}`}
                        className="btn btn-sm btn-view"
                        title={t('common.view')}
                      >
                            <i className="fas fa-eye"></i>
                          </Link>
                      <Link
                        to={`/contract/edit/${contract.id}`}
                        className="btn btn-sm btn-edit"
                        title={t('common.edit')}
                      >
                            <i className="fas fa-edit"></i>
                          </Link>
                      <button
                        className="btn btn-sm btn-print"
                        onClick={() => handlePrint(contract)}
                        title={t('common.print')}
                        disabled={printingContractId === contract.id}
                      >
                        <i className={`fas ${printingContractId === contract.id ? 'fa-spinner fa-spin' : 'fa-print'}`}></i>
                      </button>
                          <button
                        className="btn btn-sm btn-delete"
                            onClick={() => handleDelete(contract.id)}
                        title={t('common.delete')}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                    </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  {t('common.previous')}
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  {t('common.next')}
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractList;

